/**
 * The kind of asset a collection holds ("asset type" in the older naming;
 * `amp_asset_groups` in the DB), plus the pure helpers around it.
 *
 * Only browser-safe imports here (no zapatos/pg): the frontend imports this module
 * directly, like `collection-image-guardrails`. The DB-backed asset-collection
 * code lives in `asset-types.ts`, which re-exports everything below so backend
 * callers keep their single import site.
 */

import { mediaKindForContentType } from "../ad-studio/media-upload";

export const ASSET_BASE_TYPES = [
  "product",
  "image",
  "component",
  "video",
  "audio",
] as const;
export type AssetBaseType = (typeof ASSET_BASE_TYPES)[number];

/**
 * Every kind except `product`. Products live only in the backend-owned
 * catalog and never share a collection with anything else, so this is the set
 * an unconstrained collection accepts.
 */
export type NonProductAssetBaseType = Exclude<AssetBaseType, "product">;

export const NON_PRODUCT_ASSET_BASE_TYPES = ASSET_BASE_TYPES.filter(
  (baseType) => baseType !== "product",
) as readonly NonProductAssetBaseType[];

/**
 * What feeds a collection (`amp_asset_groups.sync_kind`), set when it is
 * created. A collection with no kind is one users upload into.
 */
export const ASSET_GROUP_SYNC_KINDS = [
  "product",
  "ad_platform",
  "dam",
  "getty",
] as const;
export type AssetGroupSyncKind = (typeof ASSET_GROUP_SYNC_KINDS)[number];

export function isValidAssetGroupSyncKind(
  value: string,
): value is AssetGroupSyncKind {
  return (ASSET_GROUP_SYNC_KINDS as readonly string[]).includes(value);
}

/**
 * The base-type slice of a collection. `null` means the collection accepts
 * every kind in `NON_PRODUCT_ASSET_BASE_TYPES`. A value means it accepts only
 * that kind, and is the only way a collection holds products.
 */
export interface AssetBaseTypeFields {
  baseType: AssetBaseType | null;
}

export function isValidAssetBaseType(value: string): value is AssetBaseType {
  return (ASSET_BASE_TYPES as readonly string[]).includes(value);
}

/**
 * True when the collection accepts assets of `baseType`. Used by the upload and
 * move paths to reject an asset the target collection can't hold.
 *
 * An unconstrained collection takes every kind but `product`. Products are kept
 * separate, so "any kind" never widens into the catalog: without that carve-out
 * a product could be moved into an ordinary collection and mixed with images.
 *
 * Prefer this over comparing `baseType` directly, which gets both the
 * unconstrained case and the product case wrong.
 */
export function assetTypeAcceptsBaseType(
  assetType: AssetBaseTypeFields,
  baseType: AssetBaseType,
): boolean {
  return assetType.baseType === null
    ? baseType !== "product"
    : assetType.baseType === baseType;
}

/**
 * Human-readable kind for prompts and UI. "any" is the uploadable kinds, not
 * literally every kind, because an unconstrained collection excludes products.
 */
export function describeAssetTypeBaseType(
  assetType: AssetBaseTypeFields,
): string {
  return assetType.baseType ?? "any";
}

/** True for a collection that holds products, which holds nothing else. */
export function isProductAssetType(assetType: AssetBaseTypeFields): boolean {
  return assetType.baseType === "product";
}

/**
 * Maps a MIME type to an asset base type. SVG is a `component` (it can be
 * edited/composited); video is `video`; an accepted audio format is `audio`;
 * everything else is a raster `image`.
 */
export function baseTypeForMimeType(
  mimeType: string | null | undefined,
): NonProductAssetBaseType {
  const normalized = (mimeType ?? "").toLowerCase();
  if (normalized === "image/svg+xml") {
    return "component";
  }
  if (normalized.startsWith("video/")) {
    return "video";
  }
  if (mediaKindForContentType(normalized) === "audio") {
    return "audio";
  }
  return "image";
}

/** The media types a collection would refuse, among these files. */
export function mimeTypesCollectionRefuses(
  collection: AssetBaseTypeFields,
  mimeTypes: Iterable<string>,
): string[] {
  return [
    ...new Set(
      [...mimeTypes].filter(
        (mimeType) =>
          !assetTypeAcceptsBaseType(collection, baseTypeForMimeType(mimeType)),
      ),
    ),
  ];
}

export function isImageCatalogVisibleToBrands(
  catalog: AssetBaseTypeFields & { brandProfileIds: readonly string[] },
  brandProfileIds?: readonly string[],
): boolean {
  return (
    assetTypeAcceptsBaseType(catalog, "image") &&
    (brandProfileIds === undefined ||
      catalog.brandProfileIds.length === 0 ||
      (brandProfileIds.length > 0 &&
        brandProfileIds.every((id) => catalog.brandProfileIds.includes(id))))
  );
}
